import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { useUnloadGuard } from '../../../shared/hooks';
import { useBackButtonGuard } from './useBackButtonGuard';
import { useReturnLocation } from './useReturnLocation';

// Blink/Gecko hold a blocked POP (native Back) reliably through useBlocker, so it handles
// everything there. WebKit (desktop Safari + all iOS browsers) commits the POP before the block
// lands, so on WebKit only we hand POP to the history-sentinel guard below. Firefox has no
// AppleWebKit token; Blink carries one for compat, so exclude Chrome/Chromium to leave only WebKit.
const IS_WEBKIT =
  typeof navigator !== 'undefined' &&
  /AppleWebKit/.test(navigator.userAgent) &&
  !/Chrome|Chromium/.test(navigator.userAgent);

export function useTemplateLeaveGuard({
  hasUnsavedChanges,
  hasBlockingErrors = false,
  onBlockedWithErrors,
}) {
  const navigate = useNavigate();
  const returnTo = useReturnLocation();

  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const skipBlockRef = useRef(false);
  const [isExplicitCancel, setIsExplicitCancel] = useState(false);

  useLayoutEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  });

  // On WebKit, block in-app PUSH/REPLACE only and let the sentinel guard below own POP (blocking
  // it here too would race on the same popstate and flash the modal). Everywhere else useBlocker
  // handles POP too — the reliable path that avoids the Chrome double-press slip.
  const blocker = useBlocker(
    useCallback(
      ({ historyAction }) =>
        (historyAction !== 'POP' || !IS_WEBKIT) &&
        hasUnsavedChangesRef.current &&
        !skipBlockRef.current,
      [],
    ),
  );

  const blockerRef = useRef(blocker);
  useLayoutEffect(() => {
    blockerRef.current = blocker;
  });

  // WebKit only: the native Back button isn't reliably held by useBlocker, so turn it into a normal
  // navigation back to where the user came from, which useBlocker then handles the same way as the
  // in-app Back/Cancel buttons. Armed only while dirty — a pristine form (empty create, or an admin
  // own-tenant default) is not dirty, so Back leaves freely without surfacing errors.
  const onBack = useCallback(() => {
    navigate(returnTo);
  }, [navigate, returnTo]);
  useBackButtonGuard({ when: IS_WEBKIT && hasUnsavedChanges, onBack });

  useUnloadGuard(hasUnsavedChanges);

  const skipNextBlock = useCallback(() => {
    skipBlockRef.current = true;
  }, []);

  const onLeaveTemplatesClick = useCallback(() => {
    navigate(returnTo);
  }, [navigate, returnTo]);

  const onDiscardClick = useCallback(() => {
    setIsExplicitCancel(true);
    navigate(returnTo);
  }, [navigate, returnTo]);

  const proceedNavigation = useCallback(() => {
    setIsExplicitCancel(false);
    blockerRef.current.proceed?.();
  }, []);

  const onDiscardConfirm = useCallback(() => {
    setIsExplicitCancel(false);
    blocker.proceed?.();
  }, [blocker]);

  const onDiscardClose = useCallback(() => {
    setIsExplicitCancel(false);
    blocker.reset?.();
  }, [blocker]);

  // Implicit leave (browser Back / subheader arrow / nav link) while the form has blocking errors:
  // don't show the discard modal — reveal the errors and scroll to the first one, and keep the user
  // on the page. The explicit Discard button (isExplicitCancel) still opens the modal so the user
  // can deliberately abandon changes despite errors.
  const shouldSurfaceErrors = blocker.state === 'blocked' && !isExplicitCancel && hasBlockingErrors;

  useEffect(() => {
    if (shouldSurfaceErrors) {
      onBlockedWithErrors?.();
      blocker.reset?.();
    }
  }, [shouldSurfaceErrors, onBlockedWithErrors, blocker]);

  return {
    discardDialogOpen: blocker.state === 'blocked' && !shouldSurfaceErrors,
    showSaveOnDiscard: blocker.state === 'blocked' && !isExplicitCancel,
    skipNextBlock,
    onLeaveTemplatesClick,
    onDiscardClick,
    proceedNavigation,
    onDiscardConfirm,
    onDiscardClose,
  };
}
